import { Alert, TextField } from "@mui/material";

import { useUpdateSession } from "@/shared/domain/sessions/hooks";
import { useEditableField } from "@/shared/hooks";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./EditableSessionName.module.css";

interface EditableSessionNameProps {
  name: string;
  sessionId: string;
  orgId: string;
}

export function EditableSessionName({
  name,
  sessionId,
  orgId,
}: EditableSessionNameProps) {
  const { mutateAsync: updateSession, error } = useUpdateSession();

  const { editable, currentValue, beginEdit, cancel, save } = useEditableField({
    initial: name,
    onSave: (trimmed) =>
      updateSession({
        sessionId,
        resourceOwnerId: orgId,
        // Trimmed empty string clears the name (server-side `null`);
        // otherwise the trimmed candidate persists as the new value.
        updates: { name: trimmed || null },
      }),
  });

  return (
    <div className={styles.container}>
      <NameEditor
        editable={editable}
        currentName={currentValue}
        onCancel={cancel}
        onBeginEdit={beginEdit}
        onSave={save}
      />
      <MutationErrorAlert error={error} />
    </div>
  );
}

interface NameEditorProps {
  editable: boolean;
  currentName: string;
  onBeginEdit: () => void;
  onCancel: () => void;
  onSave: (value: string) => Promise<void>;
}

function NameEditor({
  editable,
  currentName,
  onBeginEdit,
  onCancel,
  onSave,
}: NameEditorProps) {
  if (editable) {
    return (
      <TextField
        size="small"
        autoFocus
        defaultValue={currentName}
        placeholder="Session name"
        onBlur={(e) => void onSave(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            void onSave((e.target as HTMLInputElement).value);
          } else if (e.key === "Escape") {
            onCancel();
          }
        }}
        inputProps={{ style: { fontWeight: 700 } }}
        className={styles.textField}
      />
    );
  }

  return (
    <span
      className={styles.nameDisplay}
      onClick={onBeginEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onBeginEdit();
        }
      }}
    >
      {currentName || <i>No Name</i>}
    </span>
  );
}

function MutationErrorAlert({ error }: { error: Error | null }) {
  if (!error) {
    return null;
  }
  return (
    <Alert severity="error" className={styles.inlineError}>
      {userFacingErrorMessage(error, "Failed to update session name.")}
    </Alert>
  );
}
